import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { projectToday } from '../activitiesFilter';
import type { IRelatedTask } from '../model/listItem';
import { RelatedTaskRow } from '../RelatedTaskRow';
import { HoverTooltip, hoverTooltipStyles } from './HoverTooltip';
import { ReasonRows } from './ReasonRows';
import styles from './DependencyTooltip.module.css';

export interface IDependencyTooltipProps {
  /** Project tz — year elision is relative to the SITE's current year. */
  tz: string;
  /** When set, the reasons are named above the dependency breakdown so a "+N" chip reads in full. */
  reasons?: string[];
  predecessors: IRelatedTask[];
  successors: IRelatedTask[];
  children: ReactNode;
}

export function DependencyTooltip({ reasons, predecessors, successors, children, tz }: IDependencyTooltipProps) {
  const { t, i18n } = useTranslation();
  const hasContent = predecessors.length > 0 || successors.length > 0;

  if (!hasContent) return <>{children}</>;

  const currentYear = Number(projectToday(tz).slice(0, 4));

  function renderRows(tasks: IRelatedTask[]) {
    return tasks.map((task) => (
      <RelatedTaskRow
        key={task.id}
        task={task}
        language={i18n.language}
        year={currentYear}
        classes={{ row: styles.row, name: styles.rowName, meta: styles.rowMeta }}
      />
    ));
  }

  const content = (
    <>
      {/* risk.ts always emits the dependency reason ("critical-path") last, so it
          sits directly above the DEPENDS ON / CAN DELAY breakdown it explains. */}
      {reasons && reasons.length > 0 && <ReasonRows reasons={reasons} />}
      {predecessors.length > 0 && (
        <section className={hoverTooltipStyles.section}>
          <span className={styles.sectionHeader}>{t('activities.deps.predecessors')}</span>
          {renderRows(predecessors)}
        </section>
      )}
      {successors.length > 0 && (
        <section className={hoverTooltipStyles.section}>
          <span className={styles.sectionHeader}>{t('activities.deps.successors')}</span>
          {renderRows(successors)}
        </section>
      )}
    </>
  );

  return <HoverTooltip content={content}>{children}</HoverTooltip>;
}
